import {
    Form,
    Input,
    InputNumber,
    Select,
    Switch,
    Checkbox,
    Space,
    Typography,
    Row,
    Col,
    Divider,
} from "antd";

const { TextArea } = Input;
const { Text } = Typography;

const LimitRow = ({ name, label, help }) => (
    <Form.Item label={label} help={help} style={{ marginBottom: 16 }}>
        <Space align="center" wrap>
            <Form.Item
                noStyle
                shouldUpdate={(prev, cur) =>
                    prev?.limits?.[name]?.unlimited !== cur?.limits?.[name]?.unlimited
                }
            >
                {({ getFieldValue, setFieldsValue }) => {
                    const unlimited = getFieldValue(["limits", name, "unlimited"]);
                    return (
                        <Form.Item name={["limits", name, "value"]} noStyle>
                            <InputNumber
                                min={0}
                                style={{ width: 160 }}
                                disabled={!!unlimited}
                                placeholder={unlimited ? "Unlimited" : "Enter limit"}
                            />
                        </Form.Item>
                    );
                }}
            </Form.Item>
            <Form.Item
                name={["limits", name, "unlimited"]}
                valuePropName="checked"
                noStyle
            >
                <Checkbox
                    onChange={(e) => {
                        if (e.target.checked) {
                            // value cleared via form state in parent on submit (unlimited → null)
                        }
                    }}
                >
                    Unlimited
                </Checkbox>
            </Form.Item>
        </Space>
    </Form.Item>
);

const PlanFormFields = ({ isEdit = false }) => (
    <>
        <Form.Item
            name="key"
            label="Plan key"
            rules={[
                { required: true, message: "Key is required" },
                {
                    pattern: /^[a-z0-9_-]+$/,
                    message: "Lowercase letters, numbers, - and _ only",
                },
            ]}
            tooltip="Immutable after create. Used in APIs and marketing copy."
            extra={isEdit ? "Key cannot be changed after create." : undefined}
        >
            <Input placeholder="e.g. growth" disabled={isEdit} />
        </Form.Item>

        <Divider orientation="left" plain>
            Name & description
        </Divider>

        <Row gutter={16}>
            <Col xs={24} md={12}>
                <Form.Item
                    name={["name", "en"]}
                    label="Name (English)"
                    rules={[{ required: true, message: "English name is required" }]}
                >
                    <Input placeholder="Growth" />
                </Form.Item>
            </Col>
            <Col xs={24} md={12}>
                <Form.Item name={["name", "ar"]} label="Name (Arabic)">
                    <Input placeholder="النمو" dir="rtl" />
                </Form.Item>
            </Col>
            <Col xs={24} md={12}>
                <Form.Item name={["description", "en"]} label="Description (English)">
                    <TextArea rows={3} placeholder="Short plan description" />
                </Form.Item>
            </Col>
            <Col xs={24} md={12}>
                <Form.Item name={["description", "ar"]} label="Description (Arabic)">
                    <TextArea rows={3} placeholder="وصف الخطة" dir="rtl" />
                </Form.Item>
            </Col>
        </Row>

        <Divider orientation="left" plain>
            Limits
        </Divider>
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            Check Unlimited to store no cap (null). Otherwise enter a number ≥ 0.
        </Text>

        <Row gutter={16}>
            <Col xs={24} md={12}>
                <LimitRow name="ai_requests_monthly" label="AI requests / month" />
            </Col>
            <Col xs={24} md={12}>
                <LimitRow name="polar_summaries_monthly" label="Smart AI summaries / month" />
            </Col>
            <Col xs={24} md={12}>
                <LimitRow name="ga4_properties" label="GA4 properties" />
            </Col>
            <Col xs={24} md={12}>
                <LimitRow
                    name="invited_members"
                    label="Invited members"
                    help="Workspace owner does not consume a seat."
                />
            </Col>
            <Col xs={24} md={12}>
                <LimitRow
                    name="replay_sessions_month"
                    label="Session replays (max returned)"
                    help="Most recordings a date range can return. Page size stays 10."
                />
            </Col>
        </Row>

        <Divider orientation="left" plain>
            Listing
        </Divider>

        <Row gutter={16}>
            <Col xs={24} md={12}>
                <Form.Item
                    name="status"
                    label="Status"
                    initialValue="active"
                    rules={[{ required: true }]}
                    tooltip="Active plans appear on public pricing. Inactive plans are hidden."
                >
                    <Select>
                        <Select.Option value="active">Active (public)</Select.Option>
                        <Select.Option value="inactive">Inactive (hidden)</Select.Option>
                    </Select>
                </Form.Item>
            </Col>
            <Col xs={24} md={12}>
                <Form.Item
                    name="display_order"
                    label="Display order"
                    initialValue={0}
                    rules={[{ required: true }]}
                    tooltip="Lower numbers appear first."
                >
                    <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
            </Col>
            <Col xs={24} md={12}>
                <Form.Item
                    name="is_featured"
                    label="Featured"
                    valuePropName="checked"
                    extra="Highlights as “Most popular” on pricing."
                >
                    <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
            </Col>
            <Col xs={24} md={12}>
                <Form.Item
                    name="is_default"
                    label="Default plan"
                    valuePropName="checked"
                    extra="Assigned to new workspaces when unpaid (usually Free)."
                >
                    <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
            </Col>
        </Row>
    </>
);

const limitToForm = (value) => {
    if (value === null || value === undefined) {
        return { value: undefined, unlimited: true };
    }
    return { value, unlimited: false };
};

const limitFromForm = (entry) => {
    if (!entry || entry.unlimited) return null;
    if (entry.value === undefined || entry.value === null) return null;
    return Number(entry.value);
};

export const buildPlanPayload = (values, existingLimits) => {
    const descriptionEn = values.description?.en?.trim();
    const descriptionAr = values.description?.ar?.trim();
    const description =
        descriptionEn || descriptionAr
            ? {
                  en: descriptionEn || descriptionAr || "",
                  ...(descriptionAr ? { ar: descriptionAr } : {}),
              }
            : null;

    const payload = {
        name: {
            en: values.name?.en,
            ...(values.name?.ar ? { ar: values.name.ar } : {}),
        },
        description,
        limits: {
            ai_requests_monthly: limitFromForm(values.limits?.ai_requests_monthly),
            polar_summaries_monthly: limitFromForm(values.limits?.polar_summaries_monthly),
            ga4_properties: limitFromForm(values.limits?.ga4_properties),
            invited_members: limitFromForm(values.limits?.invited_members),
            replay_sessions_month: limitFromForm(values.limits?.replay_sessions_month),
            replay_bindings:
                existingLimits && Object.prototype.hasOwnProperty.call(existingLimits, "replay_bindings")
                    ? existingLimits.replay_bindings ?? null
                    : null,
        },
        status: values.status || "active",
        is_default: !!values.is_default,
        is_featured: !!values.is_featured,
        display_order: values.display_order ?? 0,
    };

    if (values.key) {
        payload.key = values.key;
    }
    return payload;
};

export const planToFormValues = (plan) => {
    const limits = plan.limits || {};
    return {
        key: plan.key,
        name: {
            en: plan.name?.en || "",
            ar: plan.name?.ar || "",
        },
        description: {
            en: plan.description?.en || "",
            ar: plan.description?.ar || "",
        },
        limits: {
            ai_requests_monthly: limitToForm(limits.ai_requests_monthly),
            polar_summaries_monthly: limitToForm(limits.polar_summaries_monthly),
            ga4_properties: limitToForm(limits.ga4_properties),
            invited_members: limitToForm(limits.invited_members),
            replay_sessions_month: limitToForm(limits.replay_sessions_month),
        },
        status: plan.status || "active",
        is_default: !!plan.is_default,
        is_featured: !!plan.is_featured,
        display_order: plan.display_order ?? 0,
    };
};

export default PlanFormFields;
